// Basic randomization (FR-06, FR-21). Shared by web and mobile so both follow
// the same rules. Randomization is seeded (by the submission id) so a respondent
// who reloads or resumes a draft sees the same order.
import { logicSources } from './logic';
import type { Answers, Definition, FlowCounts, Option, Question, Randomizer } from './types';
import { flowKey, hasOptions, jumpsOf } from './types';

/** Deterministic PRNG (mulberry32) seeded from a string hash. */
export function seededRandom(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Shuffles the movable items among their own slots; pinned items keep their index.
 */
export function shuffleWithFixed<T>(items: T[], isFixed: (item: T) => boolean, rand: () => number): T[] {
  const slots: number[] = [];
  const movable: T[] = [];
  items.forEach((item, i) => {
    if (!isFixed(item)) {
      slots.push(i);
      movable.push(item);
    }
  });
  for (let i = movable.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [movable[i], movable[j]] = [movable[j]!, movable[i]!];
  }
  const out = items.slice();
  slots.forEach((slot, k) => {
    out[slot] = movable[k]!;
  });
  return out;
}

/**
 * Question ids that take part in logic: sources and targets of display
 * conditions, carry-forward, jumps and loops, plus every question that has
 * its own condition, carry-forward or jump. Ignores `fixedPosition` and
 * `shuffleWithLogic`.
 */
export function logicLinkedIds(def: Definition): Set<string> {
  const linked = logicSources(def);
  for (const q of def.questions) {
    if (q.visibleIf?.conditions.length || q.jumpIf || q.jumpRules?.length || q.carryForward) linked.add(q.id);
    for (const j of jumpsOf(q)) if (j.targetQuestionId) linked.add(j.targetQuestionId);
  }
  return linked;
}

/**
 * Which questions stay in place when randomizing:
 * - questions marked fixedPosition,
 * - questions with display conditions, a jump or carried options, and
 * - questions other questions' logic depends on,
 * unless the question is marked shuffleWithLogic. Pinning logic-linked
 * questions keeps a condition's source shown before the question it controls;
 * for the ones allowed to move, questionOrder repairs the order afterwards.
 */
export function pinnedQuestionIds(def: Definition): Set<string> {
  const linked = logicLinkedIds(def);
  const pinned = new Set<string>();
  for (const q of def.questions) {
    if (q.fixedPosition || (linked.has(q.id) && !q.shuffleWithLogic)) pinned.add(q.id);
  }
  return pinned;
}

export interface SectionRun {
  /** Trimmed section title; empty for questions without one. */
  title: string;
  questions: Question[];
}

/** Consecutive questions sharing a section title, in definition order. */
export function sectionRuns(def: Definition): SectionRun[] {
  const runs: SectionRun[] = [];
  for (const q of def.questions) {
    const title = q.sectionTitle?.trim() ?? '';
    const last = runs[runs.length - 1];
    if (last && last.title === title) last.questions.push(q);
    else runs.push({ title, questions: [q] });
  }
  return runs;
}

export interface RandomizerGroup {
  randomizer: Randomizer;
  /** Member section titles, trimmed. */
  titles: string[];
  /** Indexes into sectionRuns(def) of the runs belonging to the members (adjacent). */
  runs: number[];
}

/**
 * The randomizers the engine applies. One is ignored when its id repeats, a
 * section is missing, blank, repeated or already in an earlier randomizer, its
 * sections are not adjacent runs, or `present` is not between 1 and the number
 * of sections. checkDefinition reports each of these.
 */
export function randomizerGroups(def: Definition, runs: SectionRun[] = sectionRuns(def)): RandomizerGroup[] {
  const out: RandomizerGroup[] = [];
  const ids = new Set<string>();
  const used = new Set<string>();
  for (const r of def.settings.randomizers ?? []) {
    if (!r || typeof r.id !== 'string' || !r.id || ids.has(r.id) || !Array.isArray(r.sections)) continue;
    const titles = r.sections.map((t) => (typeof t === 'string' ? t.trim() : ''));
    if (!titles.length || titles.some((t) => !t || used.has(t)) || new Set(titles).size !== titles.length) continue;
    if (!Number.isInteger(r.present) || r.present < 1 || r.present > titles.length) continue;
    if (titles.some((t) => !runs.some((run) => run.title === t))) continue;
    const idx = runs.flatMap((run, i) => (titles.includes(run.title) ? [i] : []));
    if (idx[idx.length - 1]! - idx[0]! + 1 !== idx.length) continue;
    ids.add(r.id);
    titles.forEach((t) => used.add(t));
    out.push({ randomizer: r, titles, runs: idx });
  }
  return out;
}

/** Titled sections that belong to an applied randomizer. */
export function randomizedSectionTitles(def: Definition): Set<string> {
  return new Set(randomizerGroups(def).flatMap((g) => g.titles));
}

/**
 * Section titles a respondent was shown for each randomizer, from the
 * `$flow:<id>` answers. A pick is valid when it lists distinct member titles;
 * randomizers without a valid pick are absent (all their sections count as shown).
 */
export function pickedSections(def: Definition, answers: Answers): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const g of randomizerGroups(def)) {
    const v = answers[flowKey(g.randomizer.id)];
    if (!Array.isArray(v) || v.some((t) => typeof t !== 'string' || !g.titles.includes(t.trim()))) continue;
    const titles = (v as string[]).map((t) => t.trim());
    if (new Set(titles).size !== titles.length) continue;
    out.set(g.randomizer.id, titles);
  }
  return out;
}

/** Section titles hidden because the respondent's randomizer picks left them out. */
export function hiddenSections(def: Definition, answers: Answers): Set<string> {
  const hidden = new Set<string>();
  const picks = pickedSections(def, answers);
  for (const g of randomizerGroups(def)) {
    const picked = picks.get(g.randomizer.id);
    if (picked) for (const t of g.titles) if (!picked.includes(t)) hidden.add(t);
  }
  return hidden;
}

/**
 * The picks to store in a new response's answers: for each randomizer, the
 * sections this respondent sees, in the order they see them. Seeded, so a
 * reload or resumed draft keeps them.
 */
export function flowPicks(def: Definition, seed: string, counts?: FlowCounts): Answers {
  const out: Answers = {};
  for (const g of randomizerGroups(def)) {
    const rand = seededRandom(`f:${g.randomizer.id}:${seed}`);
    const { evenly, limits } = g.randomizer;
    const seen = counts?.[g.randomizer.id] ?? {};
    const count = (t: string) => seen[t] ?? 0;
    const open = (t: string) => limits?.[t] === undefined || count(t) < limits[t]!;
    if (!evenly && !limits) {
      out[flowKey(g.randomizer.id)] = shuffleWithFixed(g.titles, () => false, rand).slice(0, g.randomizer.present);
      continue;
    }
    // Sections still under their limit come first; if too few are left the least-overfull ones fill the rest.
    const tie = new Map(shuffleWithFixed(g.titles, () => false, rand).map((t, i) => [t, i]));
    const ranked = [...g.titles].sort((a, b) => {
      const fa = open(a) ? 0 : 1;
      const fb = open(b) ? 0 : 1;
      if (fa !== fb) return fa - fb;
      if (fa === 1) return count(a) - limits![a]! - (count(b) - limits![b]!) || tie.get(a)! - tie.get(b)!;
      if (evenly && count(a) !== count(b)) return count(a) - count(b);
      return tie.get(a)! - tie.get(b)!;
    });
    // The respondent still sees the chosen sections in a random order.
    out[flowKey(g.randomizer.id)] = shuffleWithFixed(ranked.slice(0, g.randomizer.present), () => false, rand);
  }
  return out;
}

/** The picks in `answers` that a server accepts: the randomizer exists and the value is a full, valid pick. */
export function flowAnswers(def: Definition, answers: Answers): Answers {
  const out: Answers = {};
  const picks = pickedSections(def, answers);
  for (const g of randomizerGroups(def)) {
    const picked = picks.get(g.randomizer.id);
    if (picked && picked.length === g.randomizer.present) out[flowKey(g.randomizer.id)] = picked;
  }
  return out;
}

/**
 * Why a section keeps its position when section order is shuffled, or null
 * when it may move. Untitled questions, randomizer sections and sections
 * linked by logic stay put so a condition's source is always shown before the
 * question it controls. A logic-linked question marked shuffleWithLogic does
 * not pin its section unless its link reaches a question in another section.
 */
export function sectionPinReason(def: Definition, run: SectionRun): 'untitled' | 'randomizer' | 'logic' | 'manual' | null {
  if (!run.title) return 'untitled';
  if (randomizedSectionTitles(def).has(run.title)) return 'randomizer';
  if (def.settings.sectionOptions?.[run.title]?.keepPosition) return 'manual';
  // A loop section must stay after the question it loops over.
  if (def.settings.sectionOptions?.[run.title]?.loopOver) return 'logic';
  const linked = logicLinkedIds(def);
  const runs = sectionRuns(def);
  const runOf = new Map<string, number>();
  runs.forEach((r, i) => r.questions.forEach((q) => runOf.set(q.id, i)));
  const here = runOf.get(run.questions[0]!.id);
  // Questions on the other end of a link with each question.
  const partners = new Map<string, string[]>();
  const link = (a: string, b: string) => {
    partners.set(a, [...(partners.get(a) ?? []), b]);
    partners.set(b, [...(partners.get(b) ?? []), a]);
  };
  for (const q of def.questions) {
    for (const c of q.visibleIf?.conditions ?? []) link(q.id, c.questionId);
    if (q.carryForward) link(q.id, q.carryForward.questionId);
    for (const j of jumpsOf(q)) if (j.targetQuestionId) link(q.id, j.targetQuestionId);
  }
  for (const q of run.questions) {
    if (!linked.has(q.id)) continue;
    if (!q.shuffleWithLogic) return 'logic';
    if ((partners.get(q.id) ?? []).some((other) => runOf.has(other) && runOf.get(other) !== here)) return 'logic';
  }
  return null;
}

/** Ids a question's display conditions or carried options depend on, within `ids`. */
function sourcesIn(q: Question, ids: Set<string>): string[] {
  const out = new Set<string>();
  for (const c of q.visibleIf?.conditions ?? []) out.add(c.questionId);
  if (q.carryForward) out.add(q.carryForward.questionId);
  out.delete(q.id);
  return [...out].filter((id) => ids.has(id));
}

/**
 * Puts every question after the questions it depends on, moving as little as
 * possible. A question allowed to shuffle (shuffleWithLogic) moves to just
 * after its latest source; otherwise its sources move to just before it.
 */
function repairOrder(ids: string[], def: Definition): string[] {
  const byId = new Map(def.questions.map((q) => [q.id, q]));
  const defIndex = new Map(def.questions.map((q, i) => [q.id, i]));
  const inRun = new Set(ids);
  const src = new Map(ids.map((id) => [id, sourcesIn(byId.get(id)!, inRun)]));
  const out = ids.slice();
  const at = (id: string) => out.indexOf(id);
  const move = (id: string, to: number) => {
    out.splice(at(id), 1);
    out.splice(to, 0, id);
  };
  const moveBefore = (id: string, anchor: string) => {
    if (at(id) < at(anchor)) return;
    move(id, at(anchor));
    // Moving earlier can put it before its own sources.
    for (const s of src.get(id)!) if (at(s) > at(id)) moveBefore(s, id);
  };
  for (const id of [...ids].sort((a, b) => defIndex.get(a)! - defIndex.get(b)!)) {
    const sources = src.get(id)!;
    if (!sources.length) continue;
    if (byId.get(id)!.shuffleWithLogic && !byId.get(id)!.fixedPosition) {
      const latest = Math.max(...sources.map(at));
      if (at(id) < latest) move(id, latest); // after the removal, `latest` is the slot just after the source
    } else {
      for (const s of sources) if (at(s) > at(id)) moveBefore(s, id);
    }
  }
  // Safety net: if anything is still out of order, fall back to a stable topological sort.
  if (ids.some((id) => src.get(id)!.some((s) => at(s) > at(id)))) return topologicalOrder(out, src);
  return out;
}

/** Stable topological order: keeps the given order except where a source must come first. */
function topologicalOrder(order: string[], src: Map<string, string[]>): string[] {
  const out: string[] = [];
  const done = new Set<string>();
  const remaining = order.slice();
  while (remaining.length) {
    const i = remaining.findIndex((id) => src.get(id)!.every((s) => done.has(s)));
    const [id] = remaining.splice(i < 0 ? 0 : i, 1);
    out.push(id!);
    done.add(id!);
  }
  return out;
}

export function questionOrder(def: Definition, seed: string, picks?: Answers): string[] {
  const { randomizeQuestions, randomizeSections, sectionOptions } = def.settings;
  const baseRuns = sectionRuns(def);
  const groups = randomizerGroups(def, baseRuns);
  if (!randomizeQuestions && !randomizeSections && !groups.length) return def.questions.map((q) => q.id);
  const pinned = pinnedQuestionIds(def);
  const rand = seededRandom('q:' + seed);
  let runs = baseRuns.map((run) => {
    const ids = run.questions.map((q) => q.id);
    const shuffle = randomizeQuestions && !sectionOptions?.[run.title]?.keepQuestionOrder;
    return { run, ids: shuffle ? repairOrder(shuffleWithFixed(ids, (id) => pinned.has(id), rand), def) : ids };
  });
  if (randomizeSections) {
    runs = shuffleWithFixed(runs, (r) => sectionPinReason(def, r.run) !== null, seededRandom('s:' + seed));
  }
  // Each randomizer's sections are replaced by the ones picked for this respondent, in pick order.
  const chosen = picks ?? flowPicks(def, seed);
  for (const g of groups) {
    const picked = chosen[flowKey(g.randomizer.id)] as string[];
    const first = runs.findIndex((r) => g.titles.includes(r.run.title));
    const count = runs.filter((r) => g.titles.includes(r.run.title)).length;
    runs.splice(first, count, ...picked.flatMap((title) => runs.filter((r) => r.run.title === title)));
  }
  return runs.flatMap((r) => r.ids);
}

export function optionOrder(q: Question, seed: string): Option[] {
  const opts = q.options ?? [];
  if (!q.randomizeOptions || !hasOptions(q.type)) return opts;
  return shuffleWithFixed(opts, (o) => !!o.fixed, seededRandom(`o:${q.id}:${seed}`));
}

/** Matrix rows in the order shown to this respondent. */
export function rowOrder(q: Question, seed: string): Option[] {
  const rows = q.rows ?? [];
  if (!q.randomizeRows || q.type !== 'matrix') return rows;
  return shuffleWithFixed(rows, (o) => !!o.fixed, seededRandom(`r:${q.id}:${seed}`));
}
